import { useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button, Combobox, EmptyState, Input } from "../../../components/ui";
import { useShipmentsList } from "../../../features/shipments/queries";
import { useDeleteShipment, useCreateShipment } from "../../../features/shipments/mutations";
import {
  SHIPMENT_STAGES,
  shipmentStage,
  type Shipment,
  type ShipmentStage,
} from "../../../features/shipments/types";
import { EnvioCard } from "../components/EnvioCard";
import { KanbanView } from "../components/KanbanView";
import { trackingLinkErrorMessage } from "../utils/trackingLinkError";
import NewShipmentModal from "../components/NewShipmentModal";
import {
  trackShipmentCreated,
  trackShipmentCreateFailed,
  trackShipmentDeleted,
} from "../../../analytics/shipmentsAnalytics";

const ALL_FILTER = "all";

type StageFilter = ShipmentStage | typeof ALL_FILTER;

interface StageOption {
  value: StageFilter;
  label: string;
}

export default function EnviosListSection() {
  const { t } = useTranslation("envios");
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState<"list" | "kanban">("list");
  const [stageFilter, setStageFilter] = useState<StageFilter>(ALL_FILTER);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [companyId, setCompanyId] = useState<string | null>(null);

  const { data, isLoading, isError } = useShipmentsList();
  const deleteShipment = useDeleteShipment();
  const createShipment = useCreateShipment();
  const shipments = data?.data ?? [];

  // Sincronizar companyId desde localStorage - verificar cada 300ms
  useEffect(() => {
    const syncCompanyId = () => {
      const savedCompanyId = typeof window !== "undefined" ? localStorage.getItem("companyId") : null;
      setCompanyId(savedCompanyId);
    };

    syncCompanyId();
    const interval = setInterval(syncCompanyId, 300);

    return () => clearInterval(interval);
  }, []);

  const filteredShipments = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return shipments.filter((shipment) => {
      if (stageFilter !== ALL_FILTER && shipmentStage(shipment) !== stageFilter) {
        return false;
      }
      if (!query) return true;
      return [
        shipment.tracking_number,
        shipment.client_name,
        shipment.origin.address,
        shipment.destination.address,
      ]
        .filter(Boolean)
        .some((field) => field!.toLowerCase().includes(query));
    });
  }, [shipments, stageFilter, searchQuery]);

  const openShipment = (shipment: Shipment) =>
    navigate(`/envios/detalle/${shipment.tracking_number || shipment.id}`);

  const handleDelete = (shipment: Shipment) =>
    deleteShipment.mutate(shipment.id, {
      onSuccess: () => {
        // La etapa dice si se borran envios recien creados (error de captura)
        // o ya entregados (limpieza).
        trackShipmentDeleted(shipmentStage(shipment));
        toast.success(t("list.deleted"));
      },
      onError: () => toast.error(t("list.deleteError")),
    });

  const handleCreateShipment = (data: any): Promise<void> => {
    return new Promise((resolve, reject) => {
      createShipment.mutate(data, {
        onSuccess: (created) => {
          // Del payload solo salen enumeraciones y conteos: lleva nombre,
          // correo, telefono del cliente y las direcciones de origen y destino.
          trackShipmentCreated({
            trackingMode: String(data?.smart_tracking ?? "unknown"),
            transportationType: String(data?.transportation_type ?? "unknown"),
            hasMerchandise: Array.isArray(data?.merchandise) && data.merchandise.length > 0,
            contactCount: Array.isArray(data?.contacts) ? data.contacts.length : 0,
            notificationsEnabled: Boolean(data?.notification_frequency),
          });
          toast.success(t("list.created") || "Envío creado exitosamente");
          // La guía de paquetería es best effort: el envío ya existe aunque
          // 17TRACK haya fallado; se puede agregar después desde el detalle.
          if (created?.carrier_tracking_error) {
            toast.warning(
              created.carrier_tracking_error === "CARRIER_TRACKING_NOT_CONFIGURED"
                ? t("carrier.notConfigured")
                : t("carrier.registerFailed"),
            );
          }
          setIsModalOpen(false);
          resolve();
        },
        onError: (error: any) => {
          trackShipmentCreateFailed();
          let errorMsg = t("list.createError") || "Error al crear el envío";

          if (error?.response?.data?.meta?.message) {
            const msg = error.response.data.meta.message;
            if (typeof msg === "string") {
              errorMsg = msg;
            } else if (msg?.[422]?.[0]?.msg) {
              errorMsg = msg[422][0].msg;
            }
          } else if (error?.message) {
            errorMsg = error.message;
          }

          reject(new Error(errorMsg));
        },
      });
    });
  };

  const stageOptions: StageOption[] = [
    { value: ALL_FILTER, label: t("filterAll") },
    ...SHIPMENT_STAGES.map((stage) => ({
      value: stage,
      label: t(`stages.${stage}`),
    })),
  ];

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <div className="bg-surface border-b border-line p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 mb-4">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-content">{t("title")}</h1>
            <p className="text-sm text-content-secondary mt-1">{t("subtitle")}</p>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <div className="flex shrink-0 rounded-lg border border-line bg-card p-1">
              <Button
                variant={viewMode === "list" ? "primary" : "ghost"}
                size="sm"
                iconOnly
                icon={<i className="ri-list-check-2" />}
                onClick={() => setViewMode("list")}
                title={t("buttons.list")}
                aria-label={t("buttons.list")}
              />
              <Button
                variant={viewMode === "kanban" ? "primary" : "ghost"}
                size="sm"
                iconOnly
                icon={<i className="ri-layout-column-line" />}
                onClick={() => setViewMode("kanban")}
                title={t("buttons.kanban")}
                aria-label={t("buttons.kanban")}
              />
            </div>
            <Button
              size="sm"
              icon={<i className="ri-add-line" />}
              onClick={() => setIsModalOpen(true)}
              loading={createShipment.isPending}
              title={!companyId ? "Primero selecciona una compañía" : t("list.createPending")}
              disabled={!companyId}
            >
              {t("buttons.nuevo")}
            </Button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          <div className="shrink-0 sm:w-48">
            <Combobox
              value={stageOptions.find((option) => option.value === stageFilter) ?? null}
              onChange={(option) =>
                setStageFilter(option?.value ?? ALL_FILTER)
              }
              options={stageOptions}
              isSearchable={false}
              aria-label={t("filterAll")}
            />
          </div>
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t("searchPlaceholder")}
            icon={<i className="ri-search-line" />}
            containerClassName="min-w-0 flex-1 sm:max-w-xs"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-3 sm:p-6">
        {isLoading ? (
          <div className="flex min-h-64 items-center justify-center py-12">
            <i className="ri-loader-4-line animate-spin text-3xl text-primary-500" />
          </div>
        ) : isError ? (
          <EmptyState
            icon="ri-error-warning-line"
            title={t("list.loadError")}
          />
        ) : viewMode === "kanban" ? (
          <KanbanView shipments={filteredShipments} onCardClick={openShipment} />
        ) : filteredShipments.length === 0 ? (
          <EmptyState
            icon="ri-inbox-line"
            title={shipments.length === 0 ? t("list.empty") : t("noEnvios")}
            description={shipments.length === 0 ? t("list.emptyHint") : undefined}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredShipments.map((shipment) => (
              <EnvioCard
                key={shipment.id}
                shipment={shipment}
                onOpen={() => openShipment(shipment)}
                onCopied={() => toast.success(t("detail.linkCopied"))}
                onCopyError={(error) => toast.error(trackingLinkErrorMessage(error, t))}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>

      <NewShipmentModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleCreateShipment}
      />
    </div>
  );
}
